"use client";

import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
  wrap,
} from "motion/react";
import { type ReactNode, useRef } from "react";

import { cn } from "@/lib/utils";

// =============================================================================

type VelocityMarqueeProps = {
  children: ReactNode;
  /** Base speed in % of one copy per second, negative to go right */
  baseVelocity?: number;
  className?: string;
  /** Number of copies rendered side by side (must cover the container twice) */
  copies?: number;
};

/**
 * Infinite marquee that speeds up with the scroll velocity and follows the
 * scroll direction.
 */
export function VelocityMarquee({
  children,
  baseVelocity = 2,
  className,
  copies = 4,
}: VelocityMarqueeProps) {
  const shouldReduceMotion = useReducedMotion();
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, {
    damping: 50,
    stiffness: 400,
  });
  const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 4], {
    clamp: false,
  });
  const direction = useRef(1);

  // One copy is 100 / copies % of the track: loop over a single copy
  const span = 100 / copies;
  const x = useTransform(baseX, (value) => `${wrap(-span, 0, value)}%`);

  useAnimationFrame((_, delta) => {
    if (shouldReduceMotion) return;
    let moveBy =
      direction.current * baseVelocity * (delta / 1000) * (span / 25);

    const factor = velocityFactor.get();
    if (factor < 0) direction.current = -1;
    else if (factor > 0) direction.current = 1;

    moveBy += direction.current * moveBy * factor;
    baseX.set(baseX.get() + moveBy);
  });

  return (
    <div className={cn("flex overflow-hidden whitespace-nowrap", className)}>
      <motion.div className="flex shrink-0 whitespace-nowrap" style={{ x }}>
        {Array.from({ length: copies }, (_, index) => (
          <span
            key={index}
            aria-hidden={index > 0 || undefined}
            className="block shrink-0"
          >
            {children}
          </span>
        ))}
      </motion.div>
    </div>
  );
}

// =============================================================================
